import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, ApiError } from '../lib/api'
import { useAuthStore } from '../store/auth'
import type { OccurrenceWithEvent, FellowshipEvent, Rsvp } from '@fellowship/shared'

// ─── Search ───────────────────────────────────────────────────────────────────

export interface SearchParams {
  lat: number
  lng: number
  radius?: number
  from?: string
  to?: string
  tags?: string
  attributes?: string // comma-separated attribute keys
  eventTypes?: string // comma-separated
  denomination?: string
  sort?: 'closest' | 'soonest' | 'popular' | 'newest'
  page?: number
  limit?: number
}

export function useSearchEvents(params: SearchParams | null) {
  return useQuery({
    queryKey: ['events', 'search', params],
    queryFn: () => {
      if (!params) return Promise.resolve({ data: { occurrences: [] }, meta: { page: 1, limit: 20, total: 0 } })
      // Date inputs return YYYY-MM-DD; API expects full ISO datetimes (UTC Z required by Zod)
      const fromIso = params.from ? `${params.from}T00:00:00.000Z` : undefined
      const toIso = params.to ? `${params.to}T23:59:59.999Z` : undefined
      const qs = new URLSearchParams({
        lat: String(params.lat),
        lng: String(params.lng),
        radius: String(params.radius ?? 25),
        ...(fromIso ? { from: fromIso } : {}),
        ...(toIso ? { to: toIso } : {}),
        ...(params.tags ? { tags: params.tags } : {}),
        ...(params.attributes ? { attributes: params.attributes } : {}),
        ...(params.eventTypes ? { eventTypes: params.eventTypes } : {}),
        ...(params.denomination ? { denomination: params.denomination } : {}),
        ...(params.sort && params.sort !== 'closest' ? { sort: params.sort } : {}),
        page: String(params.page ?? 1),
        limit: String(params.limit ?? 20),
      })
      return apiFetch<{ data: { occurrences: OccurrenceWithEvent[] }; meta: { page: number; limit: number; total: number } }>(
        `/events?${qs.toString()}`,
      )
    },
    enabled: Boolean(params),
    staleTime: 2 * 60 * 1000,
  })
}

// ─── Event detail ─────────────────────────────────────────────────────────────

export function useEvent(id: string | undefined) {
  return useQuery({
    queryKey: ['events', id],
    queryFn: () => apiFetch<{ data: FellowshipEvent }>(`/events/${id}`),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
  })
}

export function useEventOccurrences(eventId: string | undefined) {
  return useQuery({
    queryKey: ['events', eventId, 'occurrences'],
    queryFn: () =>
      apiFetch<{ data: OccurrenceWithEvent[] }>(`/events/${eventId}/occurrences`),
    enabled: Boolean(eventId),
    staleTime: 2 * 60 * 1000,
  })
}

// ─── RSVP ─────────────────────────────────────────────────────────────────────

export function useCreateRsvp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: {
      occurrenceId: string
      attendanceStatus?: 'going' | 'maybe' | 'not_going'
      dishCategory?: string
      dishDescription?: string
      guestsCount?: number
      note?: string
      guestName?: string
      guestEmail?: string
    }) =>
      apiFetch<{ data: { rsvp: Rsvp; manageUrl?: string } }>('/rsvps', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['events'] })
      void queryClient.invalidateQueries({ queryKey: ['rsvps', variables.occurrenceId] })
    },
  })
}

export function useUpdateRsvp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      rsvpId,
      ...data
    }: {
      rsvpId: string
      attendanceStatus?: 'going' | 'maybe' | 'not_going'
      dishCategory?: string
      dishDescription?: string
      guestsCount?: number
      note?: string
    }) =>
      apiFetch<{ data: Rsvp }>(`/rsvps/${rsvpId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] })
      void queryClient.invalidateQueries({ queryKey: ['rsvps'] })
    },
  })
}

export function useDeleteRsvp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (rsvpId: string) =>
      apiFetch(`/rsvps/${rsvpId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] })
      void queryClient.invalidateQueries({ queryKey: ['rsvps'] })
    },
  })
}

export function useMyRsvp(occurrenceId: string | undefined) {
  const { accessToken } = useAuthStore()
  return useQuery({
    queryKey: ['rsvps', 'my', occurrenceId],
    queryFn: async () => {
      try {
        return await apiFetch<{ data: { rsvp: Rsvp } }>(`/rsvps/my?occurrenceId=${occurrenceId}`)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null
        throw err
      }
    },
    enabled: Boolean(occurrenceId && accessToken),
    staleTime: 2 * 60 * 1000,
    retry: false,
  })
}
